/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PivotTablePropDataRequestDTO} from '../models/pivot-table-prop-data-request-dto.model';
import {PropulsionScenario} from '../models/propulsion-scenario-dto-model';
import {UpdateForecastWithScenarioRequest} from '../models/update-veh-forecast-with-scenario-request.model';
import {ConfigService} from './config.service';
import {ExcelExportService} from './excel-export.service';

@Injectable({
  providedIn: 'root'
})

export class PropulsionScenarioService extends ExcelExportService {
  public propulsionScenario: PropulsionScenario;

  constructor(protected readonly configService: ConfigService,
              protected readonly http: HttpClient) {
    super(configService, http);
    // Set the endpoint for the ExcelExportService
    this.baseEndpoint = `${this.configService.baseApiUrl}` + 'propulsion-scenario';
  }

  /**
   * Sends a get request to retrieve list of all fcorgs the user has access to
   */
  getFcorgsByGmid(gmid: string): Observable<string[]> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}fcorgs/by-id`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all propulsion scenarios that the user has access to.
   */
  getPropulsionScenarios(gmid: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of propulsion scenarios that the user has access to by fcorg in the database.
   */
  getPropulsionScenariosByFcorg(gmid: string, fcorg: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid).set('fcorg', fcorg);
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all propulsion scenarios with associations
   */
  getPropulsionScenariosWithAssociations(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}propulsion-scenario/associations`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * send a delete request to delete propulsion scenario by id
   * @param propulsionScenarioId vehicle scenario id
   * @param existsInVehicleScenario boolean flag to determine whether the propulsion scenario is attached to a
   * vehicle scenario
   */
  deletePropulsionScenario(propulsionScenarioId: number, existsInVehicleScenario: boolean): Observable<any> {
    const params = new HttpParams().set('propulsionScenarioId', propulsionScenarioId).set('existsInVehicleScenario',
      existsInVehicleScenario);
    return this.http.delete(`${this.configService.baseApiUrl}propulsion-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve list of global scenario mappings that contain the propulsion scenario id
   * @param propulsionScenarioId propulsion scenario id
   */
  getGlobalVehicleScenarioMapByPropScenId(propulsionScenarioId: number): Observable<any> {
    const params = new HttpParams().set('propulsionScenarioId', propulsionScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}propulsion-scenario/global-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get list of Propulsion scenarios for the Attach Propulsion Scenario feature.
   * @param fcorg forecast organization to get propulsion scenarios from
   * @param gmid id of the user
   * @param vehScenId the vehicle scenario id that filters what propulsion scenarios can be attached to it
   * @param hidden acts as a boolean to control whether to get hidden scenarios or not
   */
  getPropScenListForAttachPropScen(fcorg: string, gmid: string, vehScenId: number, hidden: number): Observable<any> {
    const params = new HttpParams().set('fcorg', fcorg).set('gmid', gmid).set('vehScenId', vehScenId).set('hidden', hidden);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}propulsion-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * send a put request to update the propulsion scenario with new data
   * @param propulsionScenario propulsion scenario dto
   */
  updatePropulsionScenario(propulsionScenario: PropulsionScenario): Observable<PropulsionScenario> {
    return this.http.put<PropulsionScenario>(`${this.configService.baseApiUrl}propulsion-scenario`, propulsionScenario);
  }

  /**
   * Get list of propulsion scenarios that show up in the Transaction Log as having a duplicate.
   */
  getPropScenListForDupeCheck(): Observable<PropulsionScenario[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario/duplicates`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get duplicate records of the specified Propulsion Scenario
   * @param propulsionScenarioId - the ID of the propulsion scenario to retrieve data from
   */
  getPropDupeRecords(propulsionScenarioId: number): Observable<any> {
    const params = new HttpParams().set('propulsionScenarioId', propulsionScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}propulsion-scenario/duplicates`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Executes the scan for any Propulsion Scenarios that show up in the Transaction Log as having a duplicate
   */
  scanForPropScenDupes(): Observable<void> {
    return this.http.post<any>(`${this.configService.baseApiUrl}propulsion-scenario/duplicates/scan`, {})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Saves the changes made on the Excel sheet that contains information about duplicate propulsion records
   * @param file the Excel file containing the duplicate propulsion record data
   * @param sheetList a list containing the selected sheets to read data from
   * @param gmid the id of the user
   */
  savePropModifications(file: File, sheetList: string[], gmid: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    formData.append('gmid', gmid);
    return this.http.put<any>(
      `${this.configService.baseApiUrl}propulsion-scenario/duplicates`, formData, {
        reportProgress: true,
        observe: 'events'
      }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Send a put request to update the propulsion scenario with the portfolio
   * @param propulsionScenarioId the id of the propulsion scenario
   * @param vehScenId the id of the vehicle scenario the propulsion scenario is attached to
   * @param isYearRange boolean to determine if the year range is being used
   * @param gmid the id of the user
   * @param startYear the start year of the year range
   * @param endYear the end year of the year range
   */
  updatePropulsionScenarioWithPortfolio(propulsionScenarioId: number, vehScenId: number, isYearRange: boolean,
                                        gmid: string, startYear: number, endYear: number): Observable<any> {
    const requestBody = {propulsionScenarioId, vehScenId, isYearRange, gmid, startYear, endYear};
    return this.http.put<any>(`${this.configService.baseApiUrl}propulsion-scenario/with-portfolio`, requestBody)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the Start year and End Year of a propulsion scenario
   * @param propulsionScenarioId the Propulsion Scenario id
   */
  getYearRange(propulsionScenarioId: number): Observable<any> {
    const params = new HttpParams()
    .set('propulsionScenarioId', propulsionScenarioId);
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario/year-range`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the Start year and End Year of a propulsion scenario
   * @param propulsionScenarioIds list of the Propulsion Scenario ids
   */
  getYearRangeByIds(propulsionScenarioIds: number[]): Observable<any> {
    const params = new HttpParams()
    .set('propulsionScenarioIds', propulsionScenarioIds.join(','));
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario/year-range-by-ids`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Runs the global vehicle consolidation report
   *
   * @param scenIds      the ID's of the scenarios for the report
   * @param startYear    The start year for the report
   * @param endYear      The end year for the report
   * @param gmid         The user's GMID
   * @param reportType The format of the report
   * @param dateError The date error flag
   * @param incNoPens The flag to include scenarios with no penetrations
   */
  getConsolidationReport(scenIds: string, startYear: number, endYear: number, gmid: string, reportType: string,
                         dateError: number, incNoPens: number) {
    const params = new HttpParams()
    .set('scen-ids', scenIds)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('gmid', gmid)
    .set('report-type', reportType)
    .set('date-error', dateError)
    .set('inc-no-pens', incNoPens);
    return this.http.get(`${this.baseEndpoint}/consolidation-report`, {params, responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Gets the Program Code by Fuel Type Share of Propulsion Segment report
   * @param gmid The user's GMID
   * @param vehScenId The vehicle scenario ID
   * @param startYear The start year for the report
   * @param endYear The end year for the report
   */
  getPrgmCodeByFuelTypeShareReport(gmid: string, vehScenId: number, startYear: number, endYear: number) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('veh-scen-id', vehScenId)
    .set('start-year', startYear)
    .set('end-year', endYear);
    return this.http.get(`${this.baseEndpoint}/prgm-code-by-fuel-type-share-report`, {
      params,
      responseType: 'blob',
      observe: 'response'
    })
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Gets the filters for the planning markets for propulsion template create
   *
   * @param region     The region to filter by
   * @param propScenId The propulsion scenario id to filter by
   * @return a list of objects with planning market, subregion, and market area
   */
  getFiltersForPropulsionTemplate(region: string, propScenId: number): Observable<any> {
    const params = new HttpParams()
    .set('region', region)
    .set('prop-scen-id', propScenId);
    return this.http.get<any[]>(`${this.baseEndpoint}/forecast-template/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Set up the forecast template for the propulsion scenario
   *
   * @param propScenId      the propulsion scenario ID
   * @param vehScenId       the vehicle scenario ID
   * @param region          the selected region
   * @param planningMarkets the selected planning markets
   * @param brandfams       the selected brand families
   * @param startYear       the start year
   * @param endYear         the end year
   * @param gmid            the user's gmid
   * @param marketDivisions the selected market divisions
   */
  setUpPropulsionTemplate(propScenId: number, vehScenId: number, region: string, planningMarkets: string,
                          brandfams: string, startYear: number, endYear: number, gmid: string, marketDivisions: string) {
    const params = new HttpParams()
    .set('prop-scen-id', propScenId)
    .set('veh-scen-id', vehScenId)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('gmid', gmid)
    .set('region', region)
    .set('planning-markets', planningMarkets)
    .set('brandfams', brandfams)
    .set('market-divisions', marketDivisions);
    return this.http.get(`${this.baseEndpoint}/forecast-template/create/setup`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Creates a workbook with the forecast template as sheets
   *
   * @param gmid            The user's gmid
   * @param startYear       The start year for the template
   * @param endYear         The end year for the template
   * @param planningMarkets a list of market divisions to get sheets for
   */
  createPropulsionTemplate(gmid: string, startYear: number, endYear: number, planningMarkets: string[]) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('planning-markets', planningMarkets.join(","))
    .set('start-year', startYear)
    .set('end-year', endYear);
    return this.http.get(`${this.baseEndpoint}/forecast-template/create`, {params, responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Gets the scenario from the template
   * @param file The template file
   * @param sheetNames The names of the sheets to use
   */
  getScenarioFromTemplate(file: File, sheetNames: string[]) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetNames[0]);
    return this.http.post(`${this.baseEndpoint}/forecast-template/scenario`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }


  /**
   * Gets the scenario from the template
   * @param file The template file
   * @param sheetNames The names of the sheets to use
   * @param gmid The users GMID
   */
  saveForecastTemplate(file: File, sheetNames: string[], gmid: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-names', sheetNames.join(','));
    formData.append('gmid', gmid)
    return this.http.post(`${this.baseEndpoint}/forecast-template/save`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Gets the architectures for create from portfolio TVP
   */
  getTvpArchitectures(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/propulsion/tvp-arch`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the vehicle programs to select when creating TVP scenario from portfolio
   * @param arch the architecture to filter the results by
   */
  getTvpVehiclePrograms(arch: string): Observable<any> {
    const params = new HttpParams()
    .set('arch', arch);
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/propulsion/tvp-programs`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * retrieves a propulsion scenario by name
   * @param propScenName the name of the propulsion scenario
   */
  getPropulsionScenarioByName(propScenName: string) {
    const params = new HttpParams()
    .set('prop-scen-name', propScenName);
    return this.http.get(`${this.baseEndpoint}`, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates a propulsion scenario from an existing propulsion scenario
   * @param propScenName The name of the new propulsion scenario
   * @param propScenId The ID of the existing scenario
   * @param gmid The user's GMID
   * @param assumption The assumption of the new propulsion scenario
   */
  createScenarioFromScenario(propScenName: string, propScenId: number, gmid: string, assumption: string) {
    const params = new HttpParams()
    .set('prop-scen-name', propScenName)
    .set('prop-scen-id', propScenId)
    .set('gmid', gmid)
    .set('assumption', assumption);
    return this.http.post(`${this.baseEndpoint}/from-scenario`, {}, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates a propulsion scenario from the portfolio
   * @param propScenName The name of the new propulsion scenario
   * @param assumption The assumption of the new propulsion scenario
   * @param fcorg the forecast organization for the new propulsion scenario
   * @param gmid The user's GMID
   */
  createScenarioFromPortfolio(propScenName: string, assumption: string, fcorg: string, gmid: string) {
    const params = new HttpParams()
    .set('prop-scen-name', propScenName)
    .set('fcorg', fcorg)
    .set('gmid', gmid)
    .set('assumption', assumption);
    return this.http.post(`${this.baseEndpoint}/from-portfolio`, {}, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates a propulsion scenario from the portfolio
   * @param propScenName The name of the new propulsion scenario
   * @param assumption The assumption of the new propulsion scenario
   * @param gmid The user's GMID
   * @param file
   * @param sheets
   */
  createScenarioFromTemplate(propScenName: string, assumption: string, gmid: string, file: File, sheets: string[]) {
    const formData: FormData = new FormData();
    formData.append('prop-scen-name', propScenName);
    formData.append('assumption', assumption);
    formData.append('gmid', gmid);
    formData.append('file', file);
    formData.append('sheets', sheets.toString());
    return this.http.post(`${this.baseEndpoint}/from-template`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates a TVP propulsion scenario from an existing propulsion scenario
   * @param propScenName The name of the new propulsion scenario
   * @param propScenId The ID of the existing scenario
   * @param gmid The user's GMID
   * @param assumption The assumption of the new propulsion scenario
   */
  createTVPScenarioFromScenario(propScenName: string, propScenId: number, gmid: string, assumption: string) {
    const params = new HttpParams()
    .set('prop-scen-name', propScenName)
    .set('prop-scen-id', propScenId)
    .set('gmid', gmid)
    .set('assumption', assumption);
    return this.http.post(`${this.baseEndpoint}/from-scenario-tvp`, {}, {params})
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Creates a TVP propulsion scenario from the portfolio
   * @param propScenName The name of the new propulsion scenario
   * @param assumption The assumption of the new propulsion scenario
   * @param programs The programs for the new propulsion scenario
   * @param gmid The user's GMID
   */
  createTVPScenarioFromPortfolio(propScenName: string, assumption: string, programs: string, gmid: string) {
    const params = new HttpParams()
    .set('prop-scen-name', propScenName)
    .set('programs', programs)
    .set('gmid', gmid)
    .set('assumption', assumption);
    return this.http.post(`${this.baseEndpoint}/from-portfolio-tvp`, {}, {params})
    .pipe(catchError(error => this.handleError(error)));
  }


  /**
   * Gets the Excel export for the propulsion pivot table data
   * @param request  The request object for the report
   */
  getPivotTableData(request: PivotTablePropDataRequestDTO) {
    return this.http.post(`${this.baseEndpoint}/pivot-table-data`, request, {responseType: 'blob'})
    .pipe(catchError(error => this.handleErrorBlob(error)));
  }

  /**
   * Gets the filter data for populating the output filters for the Pivot Table for Propulsion Data feature
   */
  getFiltersForPivotTableData(gmid: string, workingScenId: number, isGlobal: boolean): Observable<any> {
    const params = new HttpParams().set('gmid', gmid).set('workingScenId', workingScenId).set('isGlobal', isGlobal);
    return this.http.get<any>(`${this.baseEndpoint}/pivot-table-data/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Imports a global propulsion forecast template
   * @param file The file to import
   * @param sheetNames The names of the sheets to import
   */
  importGlobalPropForecast(file: File, sheetNames: string[], isFullFile: boolean) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-names', sheetNames.join(','));
    formData.append('is-full-file', isFullFile.toString());
    return this.http.post(`${this.baseEndpoint}/global-forecast-template/import`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Validates the propulsion detail records for deletion and get the prop scen id from the file
   * @param file The file to validate
   * @param sheetNames The names of the sheets to validate
   */
  validatePropDetailRecordsForDelete(file: File, sheetNames: string[]) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-names', sheetNames.join(','));
    return this.http.post(`${this.baseEndpoint}/detail-records/validate`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Deletes propulsions records that are marked in the file
   * @param file The file to delete records from
   * @param sheetNames The names of the sheets to delete records from
   * @param gmid The user's GMID
   * @param propScenId The propulsion scenario ID
   */
  deletePropulsionRecords(file: File, sheetNames: string[], gmid: string, propScenId: number) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-names', sheetNames.join(','));
    formData.append('gmid', gmid);
    formData.append('prop-scen-id', propScenId.toString());
    return this.http.post(`${this.baseEndpoint}/detail-records/delete`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }


  /**
   * Gets a list of filtered records for a propulsion scenario
   * @param request the request DTO to hold the request parameters
   */
  getFilteredRecords(request: UpdateForecastWithScenarioRequest): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}propulsion-scenario/forecast-update/filtered-records`, request)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a list of matching records for two propulsion scenarios
   * @param request the request DTO to hold the request parameters
   */
  getSourceRecords(request: UpdateForecastWithScenarioRequest): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}propulsion-scenario/forecast-update/source-records`, request)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets the filters for the update
   * @param gmid        The user's GMID
   * @param propScenId   the scenario to get the records for
   * @param region      The region to get records for
   */
  getUpdateForecastFilters(gmid: string, propScenId: number, region: string): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('prop-scen-id', propScenId)
    .set('region', region)
    return this.http.get<any>(`${this.configService.baseApiUrl}propulsion-scenario/forecast-update/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Update the forecast with the scenario data
   * @param request the request DTO to hold the request parameters
   */
  updateForecastWithScenario(request: UpdateForecastWithScenarioRequest): any {
    return this.http.post(`${this.configService.baseApiUrl}propulsion-scenario/forecast-update`, request, {responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Updates the propulsion scenario with external data
   * @param file The template file containing the external data
   * @param sheetName The name of the sheet to read data from
   * @param gmid The user's GMID
   * @param propScenId the id of the propulsion scenario to update
   * @param isPreprocess boolean flag to determine if the data is being preprocessed
   * @param isUpdateDates boolean flag to determine if the date fields should be updated
   */
  updateWithExternalData(file: File, sheetName: string, gmid: string, propScenId: number, isPreprocess: boolean, isUpdateDates): any {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    formData.append('prop-scen-id', propScenId.toString());
    formData.append('is-preprocess', isPreprocess.toString());
    formData.append('is-update-dates', isUpdateDates.toString());
    return this.http.post(`${this.configService.baseApiUrl}propulsion-scenario/update-with-external-data`, formData, {responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }


  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.RangeNotSatisfiable:
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

  /**
   * Handles the errors produced by the backend when the response type is a blob
   * @param err The error produced by the API to be handled by the frontend
   */
  public handleErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = () => {
        const result = JSON.parse(reader.result as string);
        observer.error(result.message);
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }
}
